import BadgeDiscount from 'assets/BadgeDiscount';
import Spinner from 'components/Spinner';
import UseAuthContext from 'context/AuthContext';
import LanguageContext from 'context/LanguageContext';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getPricingPlans } from 'services/pricing-service';
import shortid from 'shortid';
import { USER_ROLES } from 'utils/constants';
import { clientConfig, formatNumber } from 'utils/utils';

// Checkmark SVG component
const CheckmarkIcon = ({ color }: { color?: string }) => (
	<svg className={`w-4 h-4 flex-shrink-0 ${color ? color : 'text-green-500'}`} fill="currentColor" viewBox="0 0 20 20">
		<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
	</svg>
);



// Features data for each plan type - using translation keys directly
const planFeatures = {
	features: [
		'PRICING_FEATURE_PRE_SIMULATION_IDF',
		'PRICING_FEATURE_24_7_CLOUD',
		'PRICING_FEATURE_UNLIMITED_REPORTS',
		'PRICING_FEATURE_SI_5282',
		'PRICING_FEATURE_SI_5280',
		'PRICING_FEATURE_SI_1045',
		'PRICING_FEATURE_REVISION_COMPARISON',
		'PRICING_FEATURE_3D_VIEWER',
		'PRICING_FEATURE_AUTOMATED_VERIFICATION',
	],
	flexibility: {
		basic: [
			'PRICING_FLEX_PAY_AS_YOU_GO',
			'PRICING_FLEX_24_PAYMENTS',
			'PRICING_FLEX_CREDITS_NEVER_EXPIRE',
		],
		professional: [
			'PRICING_FLEX_PAY_AS_YOU_GO',
			'PRICING_FLEX_24_PAYMENTS',
			'PRICING_FLEX_CREDITS_NEVER_EXPIRE',
		],
		enterprise: [
			'PRICING_FLEX_PAY_AS_YOU_GO',
			'PRICING_FLEX_24_PAYMENTS',
			'PRICING_FLEX_CREDITS_NEVER_EXPIRE',
		],
	},
};

const PricingPage = () => {
	const { user, isAuthenticated, organizationId } = useContext(UseAuthContext);
	const { t } = useTranslation();
	const { role } = useContext(UseAuthContext);
	const { language } = useContext(LanguageContext);
	const navigate = useNavigate();
	const isRTL = language === 'he';

	const { data: pricing, isLoading } = useQuery({
		queryKey: ['pricingPlans'],
		queryFn: getPricingPlans,
	});

	if (isLoading || !pricing) {
		return (
			<div className="flex justify-center items-center min-h-screen">
				<Spinner />
			</div>
		);
	}

	// Get flexibility features based on plan index
	const getFlexibilityFeatures = (index: number) => {
		if (index === 0) return planFeatures.flexibility.basic;
		if (index === 1) return planFeatures.flexibility.professional;
		return planFeatures.flexibility.enterprise;
	};

	return (
		<div className="flex flex-col gap-0 justify-center items-center w-full bg-white pt-4">
			{/* Subtitle */}
			<span className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold bg-blue-50 text-blue-600  border border-blue-200/50 shadow-sm mb-6">
				<CheckmarkIcon color="text-blue-600" />
				{t("PRICING_TRUSTED_BADGE") || "Trusted by Engineers and Laboratories since 2019"}
			</span>

			{/* Header Section */}
			<h1 className="text-[32px] md:text-[36px] font-extrabold 
      text-center text-gray-900">
				{t("PRICING_HEADER_TITLE") || "Predictable pricing for predictable approvals"}
			</h1>
			<p className="text-md text-gray-600 font-semibold  text-center max-w-3xl mt-2">
				{t("PRICING_HEADER_DESCRIPTION_1") || "Save time on every project. Pay only for what you need"}
			</p>
			{/* Pricing Cards Grid */}
			<div className={`mt-14 flex flex-col md:flex-row flex-wrap gap-6 justify-center w-full ${language === 'en' ? 'max-w-[1280px]' : 'max-w-[1245px]'}`}>
				{pricing.data.filter((item: any) => item.name !== "Single Credit").map((plan: any, index: number) => (
					<div
						key={plan.name}
						className={`relative flex flex-col rounded-xl w-full ${language === 'en' ? 'max-w-[400px]' : 'max-w-[380px]'} shadow-sm overflow-visible ${index === 1
							? 'bg-white border-2 border-[#0F65EF]'
							: 'bg-white border border-gray-200'
							}`}
					>
						{/* Most Popular Badge */}
						{index === 1 && (
							<div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
								<span className="bg-[#0E66F0] text-white px-4 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1 shadow-md">
									<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
										<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
									</svg>
									{t("MOST_POPULAR") || "Most Popular"}
								</span>
							</div>
						)}

						{/* Discount Badge */}
						{plan.percentage_discount > 0 && (
							<div className={`absolute top-0 ${isRTL ? 'left-4' : 'right-4'}`}>
								<div className="hidden md:block">
									<BadgeDiscount
										width={74}
										height={76}
										percentage={plan.percentage_discount}
									/>
								</div>
								<div className="block md:hidden">
									<BadgeDiscount
										width={55}
										height={55}
										percentage={plan.percentage_discount}
									/>
								</div>
							</div>
						)}

						{/* Card Content */}
						<div className="flex flex-col p-6 pt-8">
							{/* Plan Name & Subtitle */}
							<h3 className={`text-2xl font-bold text-gray-900`}>{plan.name}</h3>
							<p className={`text-base mt-1 text-gray-600 font-semibold`}>
								{index === 0 && (t("PRICING_PLAN_SUBTITLE_BASIC") || "For small businesses")}
								{index === 1 && (t("PRICING_PLAN_SUBTITLE_PRO") || "For established firms")}
								{index === 2 && (t("PRICING_PLAN_SUBTITLE_ENTERPRISE") || "For large consultancies")}
							</p>

							{/* Pricing */}
							<div className="mt-6">
								<div className="flex items-baseline gap-1 text-base font-semibold text-gray-600">
									<span className={`text-4xl font-semibold text-gray-900 `}>{plan.credits}</span>
									{t("PRICING_CREDITS_LABEL") || "Credits"}

									<span className="mx-[1px] ">•</span> {t("PRICING_NEVER_EXPIRE") || "Never expire"}

								</div>
								<p className={`text-base mt-1 text-gray-600 font-semibold`} >
									{formatNumber(plan.pricing.annual)}
									<span className="text-base font-semibold text-gray-600"> {t("PRICING_YEARLY") || "Ils before VAT"}</span>
								</p>
							</div>

							{/* Credits Box */}
							<div className={`mt-6 py-2 px-3 rounded-lg  bg-blue-50`}>
								{(() => {
									const hoursSaved = (plan.buildings || plan.credits) * 8;
									return (
										<div className="flex items-center gap-1 ">
											<span className="text-base font-semibold text-[#0E66F0]">
												{t("PRICING_TIME_SAVED", { hours: hoursSaved }) || `Saves approximately ${hoursSaved} hours of work`}
											</span>
										</div>
									);
								})()}
								<div className="flex items-center gap-1">
									<span className={`text-base font-semibold ${index === 1 ? 'text-[#0E66F0] font-bold' : 'text-[#0E66F0]'}`}>
										{plan.buildings || plan.credits} {t("PRICING_STANDARD_BUILDINGS") || "standard buildings"}
									</span>
									{/* Info icon with tooltip */}
									<div className="relative group/tooltip">
										<svg className={`w-4 h-4 cursor-help text-[#0E66F0]`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
										</svg>
										{/* Tooltip with m² pricing table */}
										{plan.m2_pricing && plan.m2_pricing.length > 0 && (
											<div className="absolute z-50 invisible group-hover/tooltip:visible opacity-0 group-hover/tooltip:opacity-100 transition-all duration-200 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl p-4">
												{/* Arrow */}
												<div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white border-b border-r border-gray-200 transform rotate-45"></div>
												{/* Table Header */}
												<div className={`grid grid-cols-3 border-b border-gray-200 pb-2 mb-2 ${isRTL ? 'rtl' : 'ltr'}`}>
													<div className="text-center text-sm font-semibold text-gray-600">
														{t("PRICING_TABLE_M2")}
													</div>
													<div className=" text-center text-sm font-semibold text-gray-600">
														{t("PRICING_TABLE_CREDITS")}
													</div>
													<div className="text-center text-sm font-semibold text-gray-600">
														{t("PRICING_TABLE_ILS") || "Ils"}
													</div>
												</div>
												{/* Table Rows */}
												{plan.m2_pricing.map((m2: any, idx: number) => (
													<div
														key={m2.m2_range}
														className={`grid grid-cols-3 py-1.5 ${idx === plan.m2_pricing.length - 1 ? '' : 'border-b border-gray-100'
															}`}
													>
														<div className="text-center text-sm font-semibold text-gray-600" dir="ltr">{m2.m2_range}</div>
														<div className="text-center text-sm font-semibold text-gray-600">{m2.credits}</div>
														<div className="text-center text-sm font-semibold text-gray-600" dir="ltr">{formatNumber(m2.Ils_Credit)}</div>
													</div>
												))}
											</div>
										)}
									</div>
								</div>
								<div className="flex items-center gap-1 flex-wrap text-[#0E66F0] h-6  text-sm md:text-base font-semibold">
									{plan.pricing_savings_price > 0 && plan.pricing_savings_percentage > 0 && (
										<div className="flex items-center gap-1 flex-wrap">
											<p>
												{formatNumber(plan.pricing_savings_price)} {t("PRICING_TABLE_ILS")} {t("PRICING_SAVINGS_PRICE")}
											</p>
											<span className="mx-[1px]">•</span>
											<p>
												{plan.pricing_savings_percentage}% {t("PRICING_SAVINGS_PERCENTAGE")}
											</p>
										</div>
									)}
								</div>
							</div>

							{/* Features Section */}
							<div className="mt-6">
								<h4 className={`uppercase text-base font-semibold mb-3  text-[#0E66F0]`}>
									{t("PRICING_FEATURES_TITLE") || "Features"}
								</h4>
								<ul className="space-y-2 ">
									{planFeatures.features.map((feature) => (
										<li key={feature} className={`flex items-start gap-2 font-semibold text-base leading-4 text-gray-600`}>
											<CheckmarkIcon color="text-[#0E66F0]" />
											<span>{t(feature)}</span>
										</li>
									))}
								</ul>
							</div>

							{/* Flexibility Section */}
							<div className="mt-6">
								<h4 className={`uppercase text-base font-semibold mb-3 text-[#0E66F0]`}>
									{t("PRICING_FLEXIBILITY_TITLE") || "Flexibility"}
								</h4>
								<ul className="space-y-2">
									{getFlexibilityFeatures(index).map((feature) => (
										<li key={feature} className={`flex items-start gap-2 text-base leading-4 font-semibold text-gray-600`}>
											<CheckmarkIcon color="text-[#0E66F0]" />
											<span>{t(feature)}</span>
										</li>
									))}
								</ul>
							</div>

							{/* Purchase Button */}
							<button
								className={`w-full h-12 mt-6 font-semibold rounded-lg ${index === 1
									? 'bg-[#0E66F0] text-white hover:bg-[#0052D3]'
									: 'bg-white text-[#0E66F0] border-2 border-[#0E66F0]/50 hover:bg-gray-50'
									} ${role === USER_ROLES.VIEWER ? 'opacity-50 cursor-not-allowed' : ''}`}
								disabled={role === USER_ROLES.VIEWER}
								onClick={() => {
									if (isAuthenticated) {
										const newTransactionId = shortid.generate();
										const encodedData = encodeURIComponent(`${clientConfig.pricingEnvironment}|${organizationId}|${newTransactionId}|${user?.id}`);
										window.open(`${plan.payment_link}?ReturnData=${encodedData}`, "_blank");
									} else {
										navigate("/login");
									}
								}}
							>
								{plan.credits} {t("PRICING_CREDITS")}
							</button>
						</div>
					</div>
				))}
			</div>

			{/* Unlimited Package Card */}
			<div className={`mt-10 w-full ${language === 'en' ? 'max-w-[1250px]' : 'max-w-[1190px] '} font-semibold`}>
				<div className="flex flex-col md:flex-row rounded-[20px] w-full bg-white border border-black  overflow-hidden">
					{/* Main Section - Left (2/3) */}
					<div className="flex-[2] p-8 border-l border-gray-200 ">
						<h2 className="text-2xl font-bold text-gray-900 mb-2">{t("PRICING_UNLIMITED_TITLE") || "Unlimited"}</h2>
						<p className="text-base text-gray-600  mb-8">{t("PRICING_UNLIMITED_SUBTITLE") || "Everything unlimited. One fixed price."}</p>

						{/* Features Split in Two Columns */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
							{/* Everything Plus */}
							<div>
								<span className="text-base uppercase  text-black block mb-3">{t("PRICING_UNLIMITED_EVERYTHING_PLUS") || "Everything, plus"}</span>
								<ul className="space-y-2.5 ">
									{['PRICING_UNLIMITED_BUILDINGS', 'PRICING_UNLIMITED_SIMULATIONS', 'PRICING_UNLIMITED_REPORTS'].map((feature) => (
										<li key={feature} className="flex items-start gap-3 text-base text-gray-600 leading-4">
											<CheckmarkIcon color="text-black" />
											<span>{t(feature)}</span>
										</li>
									))}
								</ul>
							</div>

							{/* Flexibility */}
							<div>
								<span className="text-base uppercase font-semibold text-black block mb-3">{t("PRICING_UNLIMITED_FLEXIBILITY") || "Flexibility"}</span>
								<ul className="space-y-2.5 ">
									{['PRICING_UNLIMITED_PREDICTABLE_COST', 'PRICING_UNLIMITED_NO_CREDIT_LIMITS'].map((feature) => (
										<li key={feature} className="flex items-start gap-3 text-base text-gray-600 leading-4">
											<CheckmarkIcon color="text-black" />
											<span>{t(feature)}</span>
										</li>
									))}
								</ul>
							</div>
						</div>
					</div>

					{/* Side Section - Right (1/3) */}
					<div className="flex-1 bg-gray-50 px-6 p-8 py-10 flex flex-col items-center text-center  border-black ">
						<div className="flex-1 flex flex-col justify-between items-center gap-2">
							<h3 className="text-xl font-bold text-gray-900">{t("PRICING_UNLIMITED_EVERYTHING_TITLE") || "Everything Unlimited"}</h3>
							<p className="text-base text-gray-600 font-semibold">{t("PRICING_UNLIMITED_NO_USAGE_LIMITS") || "No Usage Limits"}</p>
							<button
								className="w-[330px] h-12 font-semibold rounded-lg bg-black text-white hover:bg-gray-800 mb-4"
								onClick={() => {
									window.open("https://wa.me/972544780198", "_blank");
								}}
							>
								{t("PRICING_UNLIMITED_BUTTON") || "Talk to Us for Unlimited Access"}
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Payment Logos */}
			<div className={`flex flex-col sm:flex-row justify-between items-center mt-8 w-full ${language === 'en' ? 'max-w-[1280px]' : 'max-w-[1245px]'} gap-4`}>
				<div className="flex gap-3 items-center">
					<img src="/static/img/favicons/cal.png" alt="CAL" className="h-6    " />
					<img src="/static/img/favicons/isracard.png" alt="Isracard" className="h-6    " />
					<img src="/static/img/favicons/leumi.png" alt="Leumi" className="h-6    " />
					<img src="/static/img/favicons/mastercard.png" alt="Mastercard" className="h-6    " />
					<img src="/static/img/favicons/visa.png" alt="Visa" className="h-6    " />
				</div>
				<div className="flex gap-3 items-center">
					<img src="/static/img/favicons/cardcom.png" alt="Cardcom" className="h-12   " />
					<img src="/static/img/favicons/pci.png" alt="PCI" className="h-7 " />
				</div>
			</div>
		</div>
	);
};

export default PricingPage;
